/**
 * Verification Voting — fetch posts needing votes + cast votes.
 *
 * Doctrine: SCORING_ENGINE.md Invariant #7
 * - Verification integrity: no self-voting, bounded influence
 * - 48-hour voting window, daily cap (20), gym-only restriction
 * - Minimum 3 votes for callus score
 *
 * RLS policies on post_verification_votes enforce all constraints server-side.
 * Client is a thin wrapper.
 */

import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';

const log = createLogger('VERIFICATION');

export interface VerifiablePost {
  id: string;
  user_id: string;
  /** Display name of the post author */
  author_name: string;
  /** Handle of the post author */
  author_handle: string | null;
  /** Exercise / lift name */
  title: string;
  /** Optional description */
  body: string | null;
  /** Proof media URL */
  media_url: string | null;
  created_at: string;
  /** Number of existing votes on this post */
  vote_count: number;
  /** Whether the current user has already voted */
  user_voted: boolean;
}

export type VoteValue = 'legit' | 'suspicious';

/**
 * Fetch posts eligible for verification voting.
 * Criteria: created within 48 hours, not by current user, not yet voted by current user.
 */
export async function fetchVerifiablePosts(
  currentUserId: string,
): Promise<VerifiablePost[]> {
  const cutoff = new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString();

  // Fetch recent posts with proof (media_url not null)
  const { data: posts, error: postsErr } = await supabase
    .from('posts')
    .select('id, user_id, title, body, media_url, created_at')
    .neq('user_id', currentUserId)
    .gte('created_at', cutoff)
    .not('media_url', 'is', null)
    .order('created_at', { ascending: false })
    .limit(50);

  if (postsErr) {
    log.error('Failed to fetch verifiable posts', postsErr.message);
    return [];
  }
  if (!posts || posts.length === 0) return [];

  const postIds = posts.map((p: { id: string }) => p.id);
  const userIds = [...new Set(posts.map((p: { user_id: string }) => p.user_id))];

  // Batch fetch vote counts + user's own votes
  const [voteCounts, userVotes, profiles] = await Promise.all([
    supabase
      .from('post_verification_votes')
      .select('post_id')
      .in('post_id', postIds),
    supabase
      .from('post_verification_votes')
      .select('post_id')
      .in('post_id', postIds)
      .eq('voter_id', currentUserId),
    supabase
      .from('profiles')
      .select('id, display_name, handle')
      .in('id', userIds),
  ]);

  // Count votes per post
  const voteCountMap = new Map<string, number>();
  for (const v of (voteCounts.data ?? []) as { post_id: string }[]) {
    voteCountMap.set(v.post_id, (voteCountMap.get(v.post_id) ?? 0) + 1);
  }

  // Set of posts user already voted on
  const votedSet = new Set(
    ((userVotes.data ?? []) as { post_id: string }[]).map((v) => v.post_id),
  );

  // Profile lookup
  const profileMap = new Map<string, { display_name: string; handle: string | null }>();
  for (const p of (profiles.data ?? []) as { id: string; display_name: string; handle: string | null }[]) {
    profileMap.set(p.id, { display_name: p.display_name, handle: p.handle });
  }

  return posts.map((p: { id: string; user_id: string; title: string; body: string | null; media_url: string | null; created_at: string }) => {
    const profile = profileMap.get(p.user_id);
    return {
      id: p.id,
      user_id: p.user_id,
      author_name: profile?.display_name ?? 'Unknown',
      author_handle: profile?.handle ?? null,
      title: p.title ?? '',
      body: p.body,
      media_url: p.media_url,
      created_at: p.created_at,
      vote_count: voteCountMap.get(p.id) ?? 0,
      user_voted: votedSet.has(p.id),
    };
  });
}

/**
 * Cast a verification vote on a post.
 * RLS enforces: no self-voting, 48h window, daily cap (20), gym-only.
 */
export async function castVote(
  postId: string,
  voterId: string,
  vote: VoteValue,
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('post_verification_votes')
    .insert({
      post_id: postId,
      voter_id: voterId,
      vote,
    });

  if (error) {
    log.warn('Vote failed', error.message);
    // Translate common RLS errors
    if (error.message.includes('row-level security')) {
      return { success: false, error: 'Vote not permitted. You may have reached the daily limit or not share a gym with this lifter.' };
    }
    if (error.code === '23505') {
      return { success: false, error: 'You have already voted on this post.' };
    }
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Fetch current user's remaining votes today.
 */
export async function getRemainingVotes(userId: string): Promise<number> {
  const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const { count, error } = await supabase
    .from('post_verification_votes')
    .select('id', { count: 'exact', head: true })
    .eq('voter_id', userId)
    .gte('created_at', cutoff);

  if (error) {
    log.warn('Failed to fetch vote count', error.message);
    return 20; // Assume full allowance on error
  }
  return Math.max(0, 20 - (count ?? 0));
}
